import React from 'react';

/**
 * Inline SVG icon set for the app chrome.
 *
 * Everything is stroked with `currentColor`, so hover/active/disabled states
 * come from CSS `color:` — no PNG swapping and no `filter: brightness(0)
 * invert(1)` whitening hacks.
 *
 * NOT for PDF report artwork. Report imagery is pixel-positioned over scanned
 * template pages and stays as-is.
 */

const P = {
  // ── Navigation ────────────────────────────────────────────────────────
  home: (
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5.5 9.5V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5" />
    </>
  ),
  'file-plus': (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M12 12v5M9.5 14.5h5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.2 1.9" />
    </>
  ),
  package: (
    <>
      <path d="M20.5 7.8 12 12.5 3.5 7.8" />
      <path d="M12 12.5V21" />
      <path d="M20.5 16.2V7.8L12 3 3.5 7.8v8.4L12 21z" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
      <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </>
  ),
  'bar-chart': (
    <>
      <path d="M4 20h16" />
      <path d="M7 20v-6M12 20V6M17 20v-9" />
    </>
  ),
  scan: (
    <>
      <path d="M3.5 8V5.5a2 2 0 0 1 2-2H8" />
      <path d="M16 3.5h2.5a2 2 0 0 1 2 2V8" />
      <path d="M20.5 16v2.5a2 2 0 0 1-2 2H16" />
      <path d="M8 20.5H5.5a2 2 0 0 1-2-2V16" />
      <path d="M4 12h16" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 14.5a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5v.2a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1h.2a2 2 0 1 1 0 4H21a1.6 1.6 0 0 0-1.5 1z" />
    </>
  ),

  // ── Identity / header ────────────────────────────────────────────────
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
    </>
  ),
  users: (
    <>
      <circle cx="9.5" cy="8.5" r="3.5" />
      <path d="M3 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16.5 5.3a3.5 3.5 0 0 1 0 6.4" />
      <path d="M18 14.4a6.5 6.5 0 0 1 3 5.6" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  activity: <path d="M3 12h4l3 8 4-16 3 8h4" />,
  'log-out': (
    <>
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
      <path d="M10 17l-5-5 5-5" />
      <path d="M5 12h11" />
    </>
  ),

  // ── Table / list actions ─────────────────────────────────────────────
  filter: <path d="M3.5 5h17l-6.8 8v5.5l-3.4 1.7V13z" />,
  'chevron-up': <path d="M6 14.5 12 8.5l6 6" />,
  'chevron-down': <path d="M6 9.5 12 15.5l6-6" />,
  'chevron-left': <path d="M14.5 6 8.5 12l6 6" />,
  'chevron-right': <path d="M9.5 6 15.5 12l-6 6" />,
  'edit-3': (
    <>
      <path d="M12 20h8" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4z" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.75" />
    </>
  ),
  'trash-2': (
    <>
      <path d="M4 7h16" />
      <path d="M9.5 7V5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2" />
      <path d="M6.5 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4l.8-12" />
      <path d="M10.5 11v6M13.5 11v6" />
    </>
  ),
  download: (
    <>
      <path d="M12 3.5v11" />
      <path d="M7.5 10.5 12 15l4.5-4.5" />
      <path d="M4.5 19.5h15" />
    </>
  ),
  'upload-cloud': (
    <>
      <path d="M6.5 18.5A4 4 0 0 1 7 10.6a5.5 5.5 0 0 1 10.6 1.4 3.5 3.5 0 0 1-.6 6.9" />
      <path d="M12 21v-8" />
      <path d="M9 15.5 12 12.5l3 3" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </>
  ),
  save: (
    <>
      <path d="M5 3h11l3 3v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
      <path d="M8 3v5h7V3" />
      <path d="M8 21v-6h8v6" />
    </>
  ),
  sliders: (
    <>
      <path d="M4 8h9M17 8h3M4 16h3M11 16h9" />
      <circle cx="15" cy="8" r="2" />
      <circle cx="9" cy="16" r="2" />
    </>
  ),
  box: (
    <>
      <path d="M4 8.5 12 4l8 4.5v7L12 20l-8-4.5z" />
      <path d="M4 8.5 12 13l8-4.5M12 13v7" />
    </>
  ),
  pill: (
    <>
      <rect x="2.8" y="8.6" width="18.4" height="6.8" rx="3.4" transform="rotate(-45 12 12)" />
      <path d="M9.6 9.6l4.8 4.8" />
    </>
  ),

  // ── State / feedback ─────────────────────────────────────────────────
  star: <path d="m12 4 2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 9.7l5.4-.8z" />,
  check: <path d="M20 6.5 9.5 17 4 11.5" />,
  'check-circle': (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.2l2.8 2.8L16 9.8" />
    </>
  ),
  'check-square': (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
      <path d="M8 12.2l2.8 2.8L16 9.8" />
    </>
  ),
  square: <rect x="3.5" y="3.5" width="17" height="17" rx="3" />,
  'alert-triangle': (
    <>
      <path d="M12 4.2 21 19.5H3z" />
      <path d="M12 10v4" />
      <circle cx="12" cy="16.8" r=".6" fill="currentColor" stroke="none" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11.5V16" />
      <circle cx="12" cy="8.2" r=".7" fill="currentColor" stroke="none" />
    </>
  ),
  'x-circle': (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.2l5.6 5.6M14.8 9.2l-5.6 5.6" />
    </>
  ),
  x: <path d="M6 6l12 12M18 6L6 18" />,
  'inbox-empty': (
    <>
      <path d="M4 13.5h4l1.3 2.6h5.4L16 13.5h4" />
      <path d="M4.6 13.5 7 5.4A2 2 0 0 1 8.9 4h6.2a2 2 0 0 1 1.9 1.4l2.4 8.1v4.1a2 2 0 0 1-2 2H6.6a2 2 0 0 1-2-2z" />
    </>
  ),
};

export default function Icon({
  name,
  size = 20,
  strokeWidth = 1.75,
  className = '',
  title,
  ...rest
}) {
  const paths = P[name];
  if (!paths) return null;

  return (
    <svg
      className={`icon${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {paths}
    </svg>
  );
}
